// src/components/admin/DialogActivarSinPago.js
//
// D-26 — Activar un negocio sin que medie un pago: cuentas demo y cortesías.
//
// Es una excepción al modelo económico, así que el motivo es obligatorio y
// todo queda registrado (quién, cuándo, por qué y qué cuentas de cobro se
// anularon). El diálogo lo dice en pantalla para que nadie lo use por
// descuido creyendo que "arregla" una mora real.

import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  Stack, Alert, Typography, FormControlLabel, Checkbox, CircularProgress, Chip,
} from '@mui/material';
import { admin } from '../../api/authApi';

const MINIMO_MOTIVO = 5;

/**
 * @param {object}   props
 * @param {boolean}  props.abierto
 * @param {object}   props.negocio    fila del listado de negocios
 * @param {Function} props.onCerrar
 * @param {Function} props.onHecho    (mensaje) => void
 */
export default function DialogActivarSinPago({ abierto, negocio, onCerrar, onHecho }) {
  const [motivo, setMotivo]   = useState('');
  const [exenta, setExenta]   = useState(true);
  const [guardando, setGuard] = useState(false);
  const [error, setError]     = useState('');

  useEffect(() => {
    if (abierto) {
      setMotivo(''); setExenta(true); setError('');
    }
  }, [abierto]);

  const puede = motivo.trim().length >= MINIMO_MOTIVO && !guardando;

  const guardar = async () => {
    setError(''); setGuard(true);
    try {
      const r = await admin.negocioActivarSinPago(negocio.tenantID, {
        motivo: motivo.trim(),
        exentaDeCobro: exenta,
      });
      onHecho?.(r?.mensaje || 'Negocio activado sin pago.');
      onCerrar();
    } catch (e) {
      setError(e.mensajeUsuario || 'No se pudo activar el negocio.');
    } finally { setGuard(false); }
  };

  const pendiente = negocio?.cuentaPendiente;

  return (
    <Dialog open={!!abierto} onClose={guardando ? undefined : onCerrar} maxWidth="sm" fullWidth>
      <DialogTitle>Activar sin pago</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <div>
            <Typography fontWeight={700}>{negocio?.nombre}</Typography>
            <Typography variant="body2" color="text.secondary">
              {negocio?.email || negocio?.telefono || '—'}
            </Typography>
          </div>

          {pendiente && (
            <Alert severity="warning">
              Tiene la cuenta de cobro <b>{pendiente.numeroFactura}</b> sin pagar. Al continuar
              queda <b>anulada</b> con el motivo que escribas. No se marca como pagada, para no
              sumar al recaudo plata que nadie puso.
            </Alert>
          )}

          {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}

          <TextField
            label="Motivo" fullWidth autoFocus multiline minRows={2}
            value={motivo} disabled={guardando}
            onChange={(e) => setMotivo(e.target.value)}
            placeholder="Cuenta demo para pruebas internas"
            helperText={`Queda guardado con tu usuario y la fecha. Mínimo ${MINIMO_MOTIVO} caracteres.`}
          />

          <FormControlLabel
            control={
              <Checkbox checked={exenta} disabled={guardando}
                        onChange={(e) => setExenta(e.target.checked)} />
            }
            label={
              <span>
                No volver a cobrarle <Chip size="small" label="cuenta demo" sx={{ ml: 0.5 }} />
              </span>
            }
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: -1 }}>
            {exenta
              ? 'Queda exenta: no se le genera cuenta de cobro en los próximos cortes, hasta que reanudes el cobro.'
              : 'Solo cubre el mes en curso. El día 1 del mes siguiente se le vuelve a cobrar.'}
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCerrar} disabled={guardando}>Cancelar</Button>
        <Button
          variant="contained" onClick={guardar} disabled={!puede}
          startIcon={guardando ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {guardando ? 'Activando…' : 'Activar sin pago'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
